import React, { useCallback, useEffect, useRef, useState } from 'react';
import ProjectToolbar from '../ProjectToolbar/ProjectToolbar';
import { useProject } from '../../context/ProjectContext';
import type { ProjectDocument } from '../../types/project';
import styles from './JsonEditor.module.css';
import { validateProject, type ValidationIssue } from '../../api/validationApi';
import ValidationSummary from '../ValidationSummary';

// ── Schema validation via the backend endpoint ────────────────────────────────

// ── Component ─────────────────────────────────────────────────────────────────

function JsonEditor(): React.ReactElement {
  const { doc, setDoc } = useProject();

  // ── Local textarea state ──────────────────────────────────────────────────
  // The textarea holds a string that may temporarily diverge from `doc`
  // while the user is typing. We only push to context when Apply is clicked.

  const [text, setText] = useState(() => JSON.stringify(doc, null, 2));
  const [syntaxError, setSyntaxError] = useState<string | null>(null);
  const [schemaErrors, setSchemaErrors] = useState<ValidationIssue[]>([]);
  const [warnings, setWarnings] = useState<ValidationIssue[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [lastApplied, setLastApplied] = useState<string | null>(null);

  // Track whether textarea text was externally driven (context changed from
  // Visual Editor) vs. locally typed (user is editing here).
  const userEditingRef = useRef(false);

  // ── Sync: context → textarea ──────────────────────────────────────────────
  // When the Visual Editor (or Load) changes `doc`, update the textarea —
  // but only if the user is not mid-edit in the JSON Editor.
  useEffect(() => {
    if (userEditingRef.current) return;
    const serialised = JSON.stringify(doc, null, 2);
    setText(serialised);
    setSyntaxError(null);
    setSchemaErrors([]);
    setWarnings([]);
    setLastApplied(serialised);
  }, [doc]);

  // ── Textarea change handler ───────────────────────────────────────────────
  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    userEditingRef.current = true;
    setText(e.target.value);
    setSyntaxError(null);   // clear stale errors while typing
    setSchemaErrors([]);
    setWarnings([]);
  }, []);

  // ── Apply: validate then push to context ─────────────────────────────────
  const handleApply = useCallback(async () => {
    // Step 1: syntax check
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch (err) {
      setSyntaxError(
        err instanceof SyntaxError ? err.message : 'Invalid JSON syntax.',
      );
      setSchemaErrors([]);
      return;
    }
    setSyntaxError(null);

    // Step 2: schema validation via backend
    setIsValidating(true);
    try {
      const result = await validateProject(parsed);
      setWarnings(result.warnings);
      if (!result.valid) {
        setSchemaErrors(result.errors);
        return;
      }
    } catch (err) {
      // Network / endpoint error — still show a clear message but do not apply
      setSyntaxError(
        `Schema validation unavailable: ${err instanceof Error ? err.message : String(err)}`,
      );
      return;
    } finally {
      setIsValidating(false);
    }

    setSchemaErrors([]);

    // Step 3: push to context (triggers Visual Editor re-render)
    setDoc(() => parsed as ProjectDocument);
    userEditingRef.current = false;
    setLastApplied(text);
  }, [text, setDoc]);

  // ── Revert: reset textarea to current context doc ─────────────────────────
  const handleRevert = useCallback(() => {
    const serialised = JSON.stringify(doc, null, 2);
    setText(serialised);
    setSyntaxError(null);
    setSchemaErrors([]);
    setWarnings([]);
    setLastApplied(serialised);
    userEditingRef.current = false;
  }, [doc]);

  // ── Derived state ──────────────────────────────────────────────────────────
  const isDirtyLocal = text !== lastApplied;
  const hasErrors    = syntaxError !== null || schemaErrors.length > 0;

  return (
    <div className={styles.editor}>
      {/* ── Project toolbar (shared with Visual Editor) ───────────── */}
      <ProjectToolbar />
      <ValidationSummary />

      {/* ── Editor header ─────────────────────────────────────────── */}
      <div className={styles.header}>
        <span className={styles.title}>JSON Editor</span>
        <span className={styles.hint}>
          Edit the canonical project JSON. Click <strong>Apply</strong> to
          validate and update the project. Changes will be reflected in the
          Visual Editor immediately.
        </span>
        <div className={styles.actions}>
          <button
            className={[styles.btn, hasErrors && styles.btnDisabled].filter(Boolean).join(' ')}
            onClick={handleApply}
            disabled={isValidating || !isDirtyLocal}
            title="Validate and apply changes to the project"
          >
            {isValidating ? 'Validating…' : 'Apply'}
          </button>
          <button
            className={styles.btnSecondary}
            onClick={handleRevert}
            disabled={!isDirtyLocal}
            title="Discard local edits and revert to current project state"
          >
            Revert
          </button>
        </div>
      </div>

      {/* ── Main area: textarea + error panel ─────────────────────── */}
      <div className={styles.body}>
        {/* Textarea */}
        <div className={styles.textareaWrapper}>
          <textarea
            className={[
              styles.textarea,
              hasErrors ? styles.textareaError : '',
              isDirtyLocal && !hasErrors ? styles.textareaDirty : '',
            ].filter(Boolean).join(' ')}
            value={text}
            onChange={handleChange}
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            aria-label="Project JSON"
          />
        </div>

        {/* Error / status sidebar */}
        <aside className={styles.sidebar}>
          <div className={styles.sidebarHeader}>Validation</div>

          {!hasErrors && !isDirtyLocal && (
            <p className={styles.statusOk}>✓ JSON matches project state</p>
          )}

          {!hasErrors && isDirtyLocal && (
            <p className={styles.statusPending}>
              Unsaved local edits — click <strong>Apply</strong> to validate.
            </p>
          )}

          {syntaxError && (
            <div className={styles.errorBlock}>
              <div className={styles.errorTitle}>Syntax error</div>
              <pre className={styles.errorMsg}>{syntaxError}</pre>
            </div>
          )}

          {schemaErrors.length > 0 && (
            <div className={styles.errorBlock}>
              <div className={styles.errorTitle}>
                Schema errors ({schemaErrors.length})
              </div>
              <ul className={styles.errorList}>
                {schemaErrors.map((e, i) => (
                  <li key={i} className={styles.errorItem}>
                    <span className={styles.errorPath}>{e.path}</span>
                    <span className={styles.errorDetail}>{e.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {warnings.length > 0 && (
            <div className={styles.errorBlock}>
              <div className={styles.errorTitle}>Compatibility warnings ({warnings.length})</div>
              <ul className={styles.errorList}>
                {warnings.map((warning, i) => (
                  <li key={i} className={styles.errorItem}>
                    <span className={styles.errorPath}>{warning.path}</span>
                    <span className={styles.errorDetail}>{warning.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className={styles.sidebarSection}>
            <div className={styles.sidebarHeader}>Schema</div>
            <p className={styles.schemaNote}>
              Validated against{' '}
              <code>conductor-project.schema.json</code> v0.1.0
            </p>
          </div>

          <div className={styles.sidebarSection}>
            <div className={styles.sidebarHeader}>Document info</div>
            <div className={styles.infoRow}>
              <span className={styles.infoKey}>Version</span>
              <span className={styles.infoVal}>{doc.schemaVersion}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoKey}>Pages</span>
              <span className={styles.infoVal}>{doc.project.pages.length}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoKey}>Components</span>
              <span className={styles.infoVal}>
                {doc.project.pages.reduce(
                  (acc, p) => acc + p.components.length,
                  0,
                )}
              </span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoKey}>Actions</span>
              <span className={styles.infoVal}>{doc.project.actions.length}</span>
            </div>
            {doc.project.actions.length > 0 && (
              <ul className={styles.actionList}>
                {doc.project.actions.map((a) => (
                  <li key={a.id} className={styles.actionItem}>
                    <span className={styles.actionMethod}>{a.method}</span>
                    <span className={styles.actionName}>{a.name}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className={styles.infoRow}>
              <span className={styles.infoKey}>Chars</span>
              <span className={styles.infoVal}>{text.length.toLocaleString()}</span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

export default JsonEditor;
